/* AMC homepage "+" quick add, fast path (2026-10-29).
   Cards whose product has exactly one variant and nothing to configure carry data-amc-fast="1" data-amc-variant="<id>"
   (set in snippets/amc-home-quick-row.liquid). For those the "+" adds straight to the cart:
   - optimistic: the plus turns green with a count badge the instant it is tapped, then /cart/add.js runs in the background
     (one request, no product-section fetch, no cart.js pre-read, no page re-render);
   - cart drawer and header bubble are refreshed in place from the sections returned by the add call, then the drawer opens;
   - green state persists for the session and on return visits (counts come from /cart.js once per page);
   - tapping again adds one more and bumps the count; the drawer shows the quantity;
   - any failure rolls the badge back and shows a small inline notice (no overlapping text inside the button).
   Everything else (products with options, builders) keeps the existing amc-quick-add flyout. */
(() => {
  if (window.__amcPlusFast) return; window.__amcPlusFast = true;
  const SEL = 'a.amc-quick-add-action[data-amc-fast="1"][data-amc-variant]';
  const GREEN = '#1e8e3e', BLUE = '#0874df';
  const counts = new Map();
  const n = (v) => Number(v) || 0;

  const badge = (link) => {
    let b = link.querySelector('.amc-qa-count');
    if (!b) { b = document.createElement('span'); b.className = 'amc-qa-count'; b.setAttribute('aria-hidden', 'true'); link.appendChild(b); }
    return b;
  };
  const paint = (link) => {
    const q = counts.get(link.dataset.amcVariant) || 0;
    const title = link.dataset.amcTitle || 'this item';
    if (q > 0) {
      link.classList.add('is-added');
      link.style.setProperty('background', GREEN, 'important');
      badge(link).textContent = q > 99 ? '99+' : String(q);
      link.setAttribute('aria-label', `${title}: ${q} in cart. Add another`);
      link.title = `In cart (${q}). Add another`;
    } else {
      link.classList.remove('is-added');
      link.style.setProperty('background', BLUE, 'important');
      const b = link.querySelector('.amc-qa-count'); if (b) b.remove();
      link.setAttribute('aria-label', `Quick add: ${title}`);
      link.title = 'Quick add';
    }
  };
  const paintAll = () => document.querySelectorAll(SEL).forEach(paint);

  const syncFromCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    counts.clear();
    cart.items.forEach((it) => { const id = String(it.variant_id || it.id); counts.set(id, (counts.get(id) || 0) + n(it.quantity)); });
    paintAll();
  };
  let syncing = null;
  const sync = () => syncing || (syncing = fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => r.ok ? r.json() : null).then(syncFromCart).catch(() => {}).finally(() => { syncing = null; }));

  const notice = (link, msg, bad) => {
    const card = link.closest('.amc-trending-card') || link.parentElement;
    let el = card.querySelector('.amc-qa-notice');
    if (!el) { el = document.createElement('span'); el.className = 'amc-qa-notice'; el.setAttribute('role', 'status'); card.appendChild(el); }
    el.textContent = msg; el.classList.toggle('is-bad', !!bad); el.classList.add('is-on');
    clearTimeout(el._t); el._t = setTimeout(() => el.classList.remove('is-on'), bad ? 4200 : 1600);
  };

  const refreshDrawer = (sections, variantId, link) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !sections || !sections['cart-drawer'] || !sections['cart-icon-bubble']) return;
    try {
      drawer.init?.();
      drawer.classList.remove('is-empty');
      drawer.renderContents({ id: variantId, sections }, true);
      drawer.open?.(link);
    } catch (_) {
      // drawer failures must never look like add failures
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) { const t = document.createElement('div'); t.innerHTML = sections['cart-icon-bubble']; const inner = t.querySelector('#shopify-section-cart-icon-bubble') || t.firstElementChild; if (inner) bubble.innerHTML = inner.innerHTML; }
    }
    document.dispatchEvent(new CustomEvent('amc:cart:updated', { bubbles: true }));
  };

  const add = (link) => {
    const id = link.dataset.amcVariant;
    const prev = counts.get(id) || 0;
    counts.set(id, prev + 1); paint(link);
    link.classList.remove('is-pulse'); void link.offsetWidth; link.classList.add('is-pulse');
    link.dataset.amcBusy = '1';
    link.closest('[data-amc-sttabs]')?.dispatchEvent(new CustomEvent('amc:hold'));
    const body = { items: [{ id: Number(id), quantity: 1 }], sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname + window.location.search };
    const ctrl = 'AbortController' in window ? new AbortController() : null;
    const t = ctrl && setTimeout(() => ctrl.abort(), 12000);
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST', credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then((r) => r.json().then((j) => ({ ok: r.ok, j }))).then(({ ok, j }) => {
      if (!ok || (j && j.status)) throw new Error((j && (j.description || j.message)) || 'Could not add to cart');
      const item = (j.items && j.items[0]) || j;
      if (item && n(item.quantity)) counts.set(id, n(item.quantity));
      paint(link);
      notice(link, `In cart · ${counts.get(id)}`);
      refreshDrawer(j.sections, Number(id), link);
    }).catch((err) => {
      counts.set(id, prev); paint(link);
      notice(link, err && err.name === 'AbortError' ? 'Slow connection. Check your cart.' : (err && err.message) || 'Could not add. Try again.', true);
      if (err && err.name === 'AbortError') sync();
    }).finally(() => { clearTimeout(t); delete link.dataset.amcBusy; });
  };

  // Window-level capture runs before the document-level handlers in amc-quick-add.js, so the slow path never starts.
  window.addEventListener('click', (e) => {
    const link = e.target && e.target.closest ? e.target.closest(SEL) : null;
    if (!link) return;
    if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    if (link.dataset.amcBusy) return; // one in flight per button; the next tap queues naturally after it settles
    add(link);
  }, true);
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const link = e.target && e.target.closest ? e.target.closest(SEL) : null;
    if (!link) return;
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    if (!link.dataset.amcBusy) add(link);
  }, true);

  // keep counts honest when the shopper edits the cart in the drawer
  document.addEventListener('amc:cart:changed', sync);
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer'); if (!drawer || drawer.dataset.amcPlusWatch) return; drawer.dataset.amcPlusWatch = '1';
    new MutationObserver(() => { if (!drawer.classList.contains('active')) sync(); }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  const boot = () => {
    paintAll(); watchDrawer();
    if ('requestIdleCallback' in window) requestIdleCallback(sync, { timeout: 300 });
    else setTimeout(sync, 300);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', () => { paintAll(); watchDrawer(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) sync(); });
  window.amcPlusSync = sync;
})();
